import type { ChangeEvent, FormEvent } from "react"
import { Link } from "react-router-dom"
import DataTable from "@/components/common/DataTable"
import Loading from "@/components/common/Loading"
import Pagination from "@/components/common/Pagination"
import StatusBadge from "@/components/common/StatusBadge"
import { PAGE_SIZE } from "@/config/AppConfig"
import { useAdminEntryList } from "@/domain/admin/hooks/AdminEntryListHook"
import { useExcelExport } from "@/domain/excel/hooks/ExcelExportHook"
import { ExcelKindPayload } from "@bracket/idl/main/excel/payload/ExcelPayloadPt"
import { EntryStatusPayload } from "@bracket/idl/main/entry/model/EntryPt"
import {
  ENTRY_STATUS_FILTER_OPTIONS,
  entryPlayerReviewBadge,
} from "@/domain/entry/model/Entry"
import { SPORT_OPTIONS } from "@/domain/fight/model/Fight"
import type { TableColumn } from "@/components/common/DataTable"
import type { AdminEntrySearchInput } from "@/domain/admin/model/AdminEntry"
import type { EntrySummary } from "@/domain/entry/model/Entry"

const FILTER_CLASS = "focus-ring app-field w-auto"

/**
 * 선수 심사 칸 — 신청 상태와 섞이지 않게 선수 집계만 말한다.
 *
 * 취소된 신청은 심사할 대상이 아니라 집계를 내지 않는다.
 */
function renderPlayerReview(entry: EntrySummary) {
  const isCanceled = entry.status === EntryStatusPayload.ENTRY_CANCELED
  if (isCanceled) {
    return <span className="text-app-text-200">—</span>
  }

  const review = entryPlayerReviewBadge(entry)

  return (
    <span>
      <StatusBadge
        label={review.label}
        tone={review.tone}
      />
      {review.rejectedText && (
        <span className="app-txt-caption mt-1 block text-app-red-300">
          {review.rejectedText}
        </span>
      )}
    </span>
  )
}

export default function AdminEntryListPage() {
  const {
    useAdminEntryListState,
    setSearchField,
    search,
    changePage,
    resetPassword,
    clearIssuedPassword,
  } = useAdminEntryList()
  const {
    entries,
    tournaments,
    totalCount,
    currentPage,
    search: searchInput,
    isLoaded,
    errorMessage,
    issuedPassword,
  } = useAdminEntryListState()
  const {
    isExporting,
    errorMessage: excelErrorMessage,
    exportExcel,
  } = useExcelExport()

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  function onChangeSearch(
    field: keyof AdminEntrySearchInput,
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) {
    setSearchField(field, event.target.value)
  }

  function onSubmitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void search()
  }

  /** 엑셀은 대회 단위로 뽑는다 — 대회를 고르지 않으면 무엇을 뽑을지 정해지지 않는다 */
  function onClickExportExcel() {
    void exportExcel(
      ExcelKindPayload.EXCEL_KIND_ENTRY_PLAYER,
      searchInput.tournamentId,
    )
  }

  function onClickCloseIssuedPassword() {
    clearIssuedPassword()
  }

  const columns: TableColumn<EntrySummary>[] = [
    {
      dataField: "entryNo",
      caption: "신청번호",
      width: 120,
      isTitle: true,
      cellTemplate: (row) => (
        <Link
          to={`/admin/entries/${row.id}`}
          className="focus-ring font-mono hover:text-app-primary-600"
        >
          {row.entryNo}
        </Link>
      ),
    },
    { dataField: "tournamentName", caption: "대회" },
    {
      dataField: "gymName",
      caption: "체육관",
      width: 170,
      cellTemplate: (row) => (
        <span>
          <span className="block font-bold">{row.gymName}</span>
          <span className="app-txt-caption block text-app-text-200">
            {row.gymContactName}
          </span>
        </span>
      ),
    },
    { dataField: "sportLabels", caption: "종목", width: 160 },
    {
      dataField: "playerCount",
      caption: "선수",
      alignment: "right",
      width: 70,
      cellTemplate: (row) => `${row.playerCount}명`,
    },
    { dataField: "appliedAtText", caption: "접수일", width: 140 },
    {
      dataField: "statusLabel",
      caption: "신청 상태",
      width: 100,
      cellTemplate: (row) => <StatusBadge label={row.statusLabel} />,
    },
    {
      dataField: "approvedPlayerCount",
      caption: "선수 심사",
      width: 120,
      cellTemplate: (row) => renderPlayerReview(row),
    },
    {
      dataField: "id",
      caption: "관리",
      alignment: "right",
      width: 175,
      cellTemplate: (row) => (
        <span className="flex justify-end gap-2">
          <Link
            to={`/admin/entries/${row.id}`}
            className="focus-ring app-btn app-btn-sm app-btn-outline"
          >
            상세
          </Link>
          <button
            type="button"
            onClick={() => resetPassword(row.id)}
            className="focus-ring app-btn app-btn-sm app-btn-outline"
          >
            비번 초기화
          </button>
        </span>
      ),
    },
  ]

  return (
    <div className="app-page-admin max-w-[1440px]">
      <section className="app-page-head flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="app-txt-eyebrow text-app-primary-600">ENTRIES</p>
          <h2 className="app-txt-h1 mt-2">참가 신청 관리</h2>
          <p className="app-txt-body2 mt-2 text-app-text-200">
            신청을 검색하고 상세에서 선수 승인 · 체급 확정을 처리합니다.
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <button
            type="button"
            onClick={onClickExportExcel}
            disabled={isExporting || !searchInput.tournamentId}
            className="focus-ring app-btn app-btn-primary"
          >
            {isExporting ? "엑셀 만드는 중…" : "엑셀 다운로드"}
          </button>
          {!searchInput.tournamentId && (
            <p className="app-txt-caption text-app-text-200">
              대회를 선택하면 신청 선수 엑셀을 뽑을 수 있습니다.
            </p>
          )}
          {excelErrorMessage && (
            <p
              role="alert"
              className="app-txt-caption text-app-red-300"
            >
              {excelErrorMessage}
            </p>
          )}
        </div>
      </section>

      <form
        onSubmit={onSubmitSearch}
        className="app-card mb-5 flex flex-wrap items-end gap-3"
      >
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="admin-entry-tournament-id"
            className="app-label-sub"
          >
            대회
          </label>
          <select
            id="admin-entry-tournament-id"
            value={searchInput.tournamentId}
            onChange={(event) => onChangeSearch("tournamentId", event)}
            className={`${FILTER_CLASS} w-56`}
          >
            <option value="">전체</option>
            {tournaments.map((tournament) => (
              <option
                key={tournament.id}
                value={tournament.id}
              >
                {tournament.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="admin-entry-gym-name"
            className="app-label-sub"
          >
            체육관명
          </label>
          <input
            id="admin-entry-gym-name"
            type="text"
            value={searchInput.gymName}
            onChange={(event) => onChangeSearch("gymName", event)}
            className={`${FILTER_CLASS} w-40`}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="admin-entry-player-name"
            className="app-label-sub"
          >
            선수명
          </label>
          <input
            id="admin-entry-player-name"
            type="text"
            value={searchInput.playerName}
            onChange={(event) => onChangeSearch("playerName", event)}
            className={`${FILTER_CLASS} w-36`}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="admin-entry-sport"
            className="app-label-sub"
          >
            종목
          </label>
          <select
            id="admin-entry-sport"
            value={searchInput.sport}
            onChange={(event) => onChangeSearch("sport", event)}
            className={FILTER_CLASS}
          >
            <option value="">전체</option>
            {SPORT_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="admin-entry-status"
            className="app-label-sub"
          >
            신청 상태
          </label>
          <select
            id="admin-entry-status"
            value={searchInput.status}
            onChange={(event) => onChangeSearch("status", event)}
            className={FILTER_CLASS}
          >
            <option value="">전체</option>
            {ENTRY_STATUS_FILTER_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="focus-ring app-btn app-btn-primary"
        >
          검색
        </button>
      </form>

      {errorMessage && (
        <p
          role="alert"
          className="app-alert app-alert-error mb-5"
        >
          {errorMessage}
        </p>
      )}

      {issuedPassword && (
        <div
          role="status"
          className="app-alert app-alert-success mb-5 flex flex-wrap items-center justify-between gap-3"
        >
          <p className="app-txt-body2">
            임시 비밀번호가 발급되었습니다:{" "}
            <strong className="font-mono">{issuedPassword}</strong> — 체육관에
            전달해 주세요. 이 값은 다시 확인할 수 없습니다.
          </p>
          <button
            type="button"
            onClick={onClickCloseIssuedPassword}
            className="focus-ring app-btn app-btn-sm border-app-green-200 text-app-green-300 hover:bg-app-green-100"
          >
            확인
          </button>
        </div>
      )}

      {!isLoaded && <Loading label="신청 목록을 불러오는 중" />}

      {isLoaded && (
        <>
          <div className="app-surface overflow-x-auto">
            <DataTable
              rowIdField="id"
              dataSource={entries}
              columns={columns}
              caption="참가 신청 목록"
              emptyMessage="조회된 신청이 없습니다."
            />
          </div>

          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={changePage}
            />
          </div>
        </>
      )}
    </div>
  )
}
